import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, XCircle, LoaderPinwheel } from 'lucide-react';
import Header from '../components/Header';
import Button from '../components/elements/Button';
import { confirmActive } from '../utils/publicApi';

export default function ConfirmActivePage() {
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [message, setMessage] = useState('');
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [email] = useState(searchParams.get('email') || '');
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Confirm Active | Eternal Locker™";
    }, []);

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Invalid confirmation link. No token provided.');
            return;
        }

        confirmActivity();
    }, [token]);

    const confirmActivity = async () => {
        try {
            confirmActive(token, email)
                .then(async (response) => {
                    if (response.success) {
                        setStatus('success');
                        setMessage(response.message || 'Your account activity has been confirmed successfully!');
                    } else {
                        setStatus('error');
                        setMessage(response.message || 'Failed to confirm activity. The link may have expired.');
                    }
                }).catch((error) => {
                    setStatus('error');
                    setMessage('An error occurred while confirming your activity. Please try again later.');
                });
        } catch (error) {
            setStatus('error');
            setMessage('An error occurred while confirming your activity. Please try again later.');
        }
    };

    return (
        <>
            <Header type="large" />
            <div className="min-h-screen bg-background-light flex items-center justify-center p-4">
                <div className="max-w-2xl w-full bg-card-background rounded-xl shadow-2xl p-8 md:p-12 border border-accent-main">
                    {status === 'loading' && (
                        <div className="text-center">
                            <LoaderPinwheel className="w-20 h-20 text-accent-main mx-auto mb-6 animate-spin" />
                            <h1 className="text-3xl font-bold text-primary-dark mb-4">Confirming Activity...</h1>
                            <p className="text-lg text-text-muted">
                                Please wait while we process your confirmation.
                            </p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="text-center">
                            <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
                            <h1 className="text-3xl font-bold text-primary-dark mb-4">Activity Confirmed!</h1>
                            <p className="text-lg text-text-muted mb-8">
                                {message}
                            </p>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                                <h3 className="text-lg font-semibold text-green-800 mb-2">What This Means</h3>
                                <p className="text-green-700 text-left">
                                    Your account has been marked as active for this month. You won't receive any more activity check emails until next month. Your nominee access settings remain unchanged, and your data is secure.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button onClick={() => navigate('/')}>
                                    Go to Dashboard
                                </Button>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="text-center">
                            <XCircle className="w-20 h-20 text-red-600 mx-auto mb-6" />
                            <h1 className="text-3xl font-bold text-primary-dark mb-4">Confirmation Failed</h1>
                            <p className="text-lg text-text-muted mb-8">
                                {message}
                            </p>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                                <h3 className="text-lg font-semibold text-red-800 mb-3">Possible Reasons</h3>
                                <ul className="text-red-700 text-left space-y-2">
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        The confirmation link has expired or been used already
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        The link was incorrect or incomplete
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        Your activity has already been confirmed for this month
                                    </li>
                                </ul>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button onClick={() => navigate('/')}>
                                    Go to Dashboard
                                </Button>
                                <Button type="secondary" onClick={() => navigate('/contact')}>
                                    Contact Support
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Info Section */}
                    <div className="mt-8 pt-8 border-t border-accent-main">
                        <h3 className="text-lg font-semibold text-primary-dark mb-4 text-center">About Monthly Activity Checks</h3>
                        <div className="bg-background-light rounded-lg p-6">
                            <p className="text-text-dark text-sm mb-4">
                                Eternal Locker™ sends monthly activity checks to ensure your account is still active and accessible. This is a security measure to protect your data and ensure your designated nominees can access your information if needed.
                            </p>
                            <ul className="text-text-dark text-sm space-y-2">
                                <li className="flex items-start">
                                    <span className="text-accent-main mr-2 font-bold">→</span>
                                    <span><strong>1st of month:</strong> Initial activity check email sent</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-accent-main mr-2 font-bold">→</span>
                                    <span><strong>10th of month:</strong> First reminder if no response</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-accent-main mr-2 font-bold">→</span>
                                    <span><strong>15th of month:</strong> Final reminder if no response</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-accent-main mr-2 font-bold">→</span>
                                    <span><strong>20th of month:</strong> Nominee access automatically granted if still no response</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
